import { Box, Typography } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { isUserTeamManty, sortUserListByEmail } from 'helpers/users';

import AddIcon from '@mui/icons-material/Add';
import { ButtonMenu } from 'components/ButtonMenu';
import CreateMultipleUserModal from './CreateMultipleUser/CreateMultipleUserModal';
import CreateUserFormProvider from './CreateOrEditUser/CreateUserContext/CreateUserFormProvider';
import CreateUserModal from './CreateOrEditUser/CreateUserModal';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchBar from 'components/SharedComponents/SearchBar/SearchBar';
import StyBoxAdmin from 'routes/Admin/shared/StyBoxAdmin';
import StyGridTitleContainer from 'routes/Admin/shared/StyGridTitleContainer';
import UserCount from './UsersGrid/components/UserCount';
import UsersGrid from './UsersGrid/UsersGrid';
import { checkStringMatch } from 'routes/Library/helpers';
import { keepOnlyBoughtScopes } from './helpers';
import { styled } from '@mui/material/styles';
import { useFetchUsers } from '../../../Profile/hooks/fetchUsers';
import { useIntegrationFunctions } from 'helpers/integrations';
import { useSelector } from 'store/hooks';
import { useTranslation } from 'react-i18next';

const StyHeader = styled(Box, {
    name: 'StyHeader',
})({
    display: 'flex',
    justifyContent: 'space-between',
    flex: 1,
    margin: '1rem',
});

const StyManageUsersContent = styled(Box, {
    name: 'StyGrid',
})({
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    width: '100%',
});

const StySearchBarContainer = styled(Box, {
    name: 'StySearchBarContainer',
})({
    alignSelf: 'center',
    display: 'flex',
});

const StyButtonMenu = styled(ButtonMenu, {
    name: 'StyButtonMenu',
})(({ theme }) => ({
    marginLeft: theme.spacing(2),
    whiteSpace: 'nowrap',
}));

const ManageUsers = (): JSX.Element => {
    const { t } = useTranslation();

    const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
    const [isCreateMultipleUserModalOpen, setIsCreateMultipleUserModalOpen] =
        useState(false);
    const [search, setSearch] = useState('');

    const { data: users = [], isLoading: isFetchingUsers } = useFetchUsers();

    const existingUsersEmails = useMemo(
        () => users.map((user) => user.email),
        [users],
    );

    const currentScopes = useSelector((state) => state.vision.scopes);
    const isCurrentUserTeamManty = useSelector((state) =>
        isUserTeamManty(state.vision.user.email),
    );
    const { data: integrationFunctions, isLoading: areIntegrationsLoading } =
        useIntegrationFunctions();

    const visibleUsers = sortUserListByEmail(
        isCurrentUserTeamManty
            ? users
            : users.filter((user) => !isUserTeamManty(user.email)),
    );
    const filteredUsers = visibleUsers.filter((user) =>
        checkStringMatch(user.email, search),
    );
    const userCount = filteredUsers.length;

    const clientScopes = keepOnlyBoughtScopes(
        currentScopes,
        integrationFunctions || new Set(),
    );
    const isLoading = isFetchingUsers || areIntegrationsLoading;

    const menuItems = [
        {
            name: t('adminSpace.createUser.menuLabel'),
            icon: <PersonAddIcon />,
            action: () => setIsCreateUserModalOpen(true),
            className: 'pendo-create-new-user-start',
        },
        {
            name: t('adminSpace.createUsers.menuLabel'),
            icon: <GroupAddIcon />,
            action: () => setIsCreateMultipleUserModalOpen(true),
            className: 'pendo-create-multiple-user-start',
        },
    ];

    return (
        <StyBoxAdmin data-testid="e2e-users-management">
            <StyManageUsersContent>
                <StyHeader>
                    <StyGridTitleContainer
                        sx={{
                            alignItems: 'baseline',
                            gap: '1em',
                        }}
                    >
                        <Typography
                            variant="h4"
                            sx={(theme) => ({
                                color: theme.palette.darkGrey,
                                '&:first-letter': {
                                    textTransform: 'initial', // otherwise we have a rogue empty space at the end of the text
                                },
                            })}
                        >
                            {t('adminSpace.usersList.title')}
                        </Typography>
                        <UserCount count={userCount} />
                    </StyGridTitleContainer>
                    <StySearchBarContainer>
                        <SearchBar
                            width="md"
                            onChange={setSearch}
                            placeholder={t('adminSpace.usersList.searchUser')}
                        />
                    </StySearchBarContainer>
                    <StyButtonMenu
                        menuItems={menuItems}
                        buttonLabel={t('adminSpace.createUser.buttonLabel')}
                        extra={{
                            variant: 'contained',
                        }}
                        iconElement={<AddIcon />}
                    />
                </StyHeader>
                <UsersGrid
                    users={filteredUsers}
                    clientScopes={clientScopes}
                    isLoading={isLoading}
                />
            </StyManageUsersContent>
            {isCreateUserModalOpen && (
                <CreateUserFormProvider>
                    <CreateUserModal
                        setIsModalOpen={setIsCreateUserModalOpen}
                        existingUsersEmails={existingUsersEmails}
                    />
                </CreateUserFormProvider>
            )}
            {isCreateMultipleUserModalOpen && (
                <CreateMultipleUserModal
                    setIsModalOpen={setIsCreateMultipleUserModalOpen}
                    existingUsersEmails={existingUsersEmails}
                />
            )}
        </StyBoxAdmin>
    );
};

export default ManageUsers;
